import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationSelector from './LocationSelector';
import { getUserGroups, UserGroup, AUTH_ERRORS, inviteUsersService, InviteUsersRequest, InviteUsersResponse } from '../services/axiosService';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';
import { Application } from '../constants/Application';
import { trackEvent } from '../services/trackEvent';

interface InviteUsersProps {
  open: boolean;
  onClose: () => void;
  onInvitationSent?: () => void; // Callback after successful invitation
}

const InviteUsers: React.FC<InviteUsersProps> = ({ open, onClose, onInvitationSent }) => {
  const { t } = useTranslation();
  const { getAccessToken, user } = useAuth();
  const { getAccount, userRole, getDisplayName } = useUser();

  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedLocationDetails, setSelectedLocationDetails] = useState<Array<{id: string, name: string}>>([]);
  const [emails, setEmails] = useState<string>('');
  const [userGroup, setUserGroup] = useState<string>('');
  const [contactInfo, setContactInfo] = useState<string>('');
  const [errors, setErrors] = useState<{ emails?: boolean; jobFunction?: boolean }>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [loadingUserGroups, setLoadingUserGroups] = useState<boolean>(false);
  const [currentApplication, setCurrentApplication] = useState<string>(Application.Protect);
  const [account, setAccount] = useState<{ accountId: number; applicationName: string } | null>(null);
  const errorRef = useRef<HTMLParagraphElement | null>(null);
  const successRef = useRef<HTMLParagraphElement | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      setSelectedLocations([]);
      setSelectedLocationDetails([]);
      setEmails('');
      setUserGroup('');
      setErrors({});
      setApiError(null);
      setSuccessMessage(null);

      // Pre-fill contact information with logged-in user's details
      const displayName = getDisplayName();
      const userEmail = user?.email || '';
      const contactText = `${displayName}\n${userEmail}`;
      setContactInfo(contactText);

      // Get and store account object
      const application = getAccount(currentApplication);
      setAccount(application);
      fetchUserGroups(application);
    }
  }, [open]);

  useEffect(() => {
    if (apiError && errorRef.current) {
      errorRef.current.focus();
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [apiError]);

  useEffect(() => {
    if (successMessage && successRef.current) {
      successRef.current.focus();
      successRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [successMessage]);

  const fetchUserGroups = async (acc?: { accountId: number; applicationName: string }) => {
    setLoadingUserGroups(true);
    setApiError(null);
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) throw new Error(AUTH_ERRORS.NO_ACCESS_TOKEN);
      const accountObj = acc || account;
      if (!accountObj) throw new Error('apiResponse.inviteUsers.errors.accountNotAvailable');
      const groups = await getUserGroups(accountObj.accountId, accountObj.applicationName, accessToken);
      setUserGroups(groups);
    } catch (error: any) {
      let message = t('inviteUsers.messages.loadingUserGroups');
      if (error?.response?.data?.message) message = error.response.data.message;
      else if (error?.message) message = error.message;
      setApiError(message);
    } finally {
      setLoadingUserGroups(false);
    }
  };

  const handleLocationSelect = (locations: string[], locationDetails?: Array<{id: string, name: string}>) => {
    setSelectedLocations(locations);
    setSelectedLocationDetails(locationDetails || []);
  };

  const isValidEmail = (email: string) => {
    const atIndex = email.indexOf('@');
    if (atIndex > 0) {
      const localPart = email.substring(0, atIndex);
      const domainPart = email.substring(atIndex + 1);
     
      // Check local part for invalid dot patterns
      if (localPart.includes('..') || localPart.startsWith('.') || localPart.endsWith('.')) {
        return false;
      }
     
      // Check domain part for invalid dot patterns
      if (domainPart.includes('..') || domainPart.startsWith('.') || domainPart.endsWith('.')) {
        return false;
      }
    } 
    return /^[^\s@]+@[a-zA-Z0-9]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?$/.test(email);
  };

  const validateEmails = (emailString: string): { valid: boolean; invalidEmails: string[] } => {
    if (!emailString.trim()) {
      return { valid: false, invalidEmails: [] };
    }

    const emailList = emailString
      .split(/[,\n]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    const invalidEmails = emailList.filter(email => !isValidEmail(email));
    
    return {
      valid: emailList.length > 0 && invalidEmails.length === 0,
      invalidEmails
    };
  };

  const handleSendInvitation = async (event?: React.MouseEvent) => {
    // Prevent multiple submissions
    if (isSubmitting) {
      console.log('Already submitting, ignoring duplicate call');
      return;
    }

    // Prevent default behavior and stop propagation
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    console.log('Starting invitation submission...');
    setApiError(null);
    setSuccessMessage(null);

    const emailValidation = validateEmails(emails);
    const newErrors = {
      emails: !emailValidation.valid,
      jobFunction: !userGroup.trim(),
    };
    setErrors(newErrors);

    if (newErrors.emails || newErrors.jobFunction) {
      console.log('Validation failed, stopping submission');
      return;
    }

    if (!account) {
      setApiError('apiResponse.inviteUsers.errors.accountNotAvailable');
      return;
    }

    const emailList = emails
      .split(/[,\n]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    console.log('Sending invitations to:', emailList);

    const invitationRequest: InviteUsersRequest = {
      Emails: emailList,
      AccountRoleGroupId: parseInt(userGroup),
      LocationIds: selectedLocations.map(Number),
      InviterContactInfo: contactInfo,
      ExpirationDays: 30, // Default to 30 days
      ApplicationName: account.applicationName,
      AccountId: account.accountId,
    };

    setIsSubmitting(true);

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) throw new Error(AUTH_ERRORS.NO_ACCESS_TOKEN);
      
      // Call the actual API
      const response: InviteUsersResponse = await inviteUsersService.inviteUsers(invitationRequest, accessToken);
      
      const locationInfo = selectedLocationDetails.map(loc => `${loc.id} - ${loc.name}`);
      trackEvent.custom('CUM Invitations Sent', {
        inviterRole: userRole?.isAdmin ? 'Admin' : 'Basic User',
        emailCount: emailList.length,
        hasLocations: selectedLocations.length > 0,
        locationCount: selectedLocations.length,
        locationIds: selectedLocations,
        locationNames: selectedLocationDetails.map(loc => loc.name),
        locationInfo: locationInfo,
        applicationName: account.applicationName,
        userGroup: userGroup,
        emails: emailList,
        successCount: response.successCount,
        failedCount: response.failedCount,
        totalSent: response.totalInvitationsSent
      });

      // Track location user management for each assigned location
      if (selectedLocationDetails.length > 0) {
        selectedLocationDetails.forEach(location => {
          trackEvent.locationUserManaged('assign', 'invited-users', `${location.id} - ${location.name}`, {
            inviterRole: userRole?.isAdmin ? 'Admin' : 'Basic User',
            applicationName: account.applicationName,
            isInvitation: true,
            emailCount: emailList.length,
            locationId: location.id,
            locationName: location.name,
            successCount: response.successCount,
            failedCount: response.failedCount
          });
        });
      }
debugger;
      // Clear form fields immediately after successful response
      if (response.success) {
        // Clear the form fields
        setEmails('');
        setUserGroup('');
        setSelectedLocations([]);
        setSelectedLocationDetails([]);
        
        // Re-populate contact info for next invitation
        const displayName = getDisplayName();
        const userEmail = user?.email || '';
        const contactText = `${displayName}\n${userEmail}`;
        setContactInfo(contactText);
      }

      // Handle response with detailed feedback
      if (response.success && response.failedCount === 0) {
        setSuccessMessage(t('apiResponse.inviteUsers.success.allInvitationsSent', { count: response.successCount }));
      } else if (response.successCount > 0 && response.failedCount > 0) {
        setSuccessMessage(t('apiResponse.inviteUsers.success.partialSuccess', {
          successCount: response.successCount,
          failedCount: response.failedCount
        }));
        
        // Show details about failed invitations
        const failedDetails = response.failedInvites.map(failed => `${failed.email}: ${t(failed.errorMessage)}`).join('\n');
        setApiError(`${t('apiResponse.inviteUsers.errors.someInvitationsFailed')}\n${failedDetails}`);
      } else if (response.failedCount > 0) {
        const failedDetails = response.failedInvites.map(failed => `${failed.email}: ${t(failed.errorMessage)}`).join('\n');
        setApiError(`${t('apiResponse.inviteUsers.errors.allInvitationsFailed')}\n${failedDetails}`);
      } else {
        setSuccessMessage(response.message || t('apiResponse.inviteUsers.success.invitationsProcessed'));
      }
      
      // Only close dialog and refresh if ALL invitations were successful (no failures)
      if (response.success && response.failedCount === 0) {
        setTimeout(() => {
          onClose();
          if (onInvitationSent) {
            onInvitationSent();
          }
        }, 3000); // Give more time to read the detailed message
      }
      
    } catch (error: any) {
      let message = "apiResponse.inviteUsers.errors.failedToSendInvitations";
      if (error?.response?.status === 500 && error?.response?.data?.message) {
        message = "apiResponse.inviteUsers.errors.serverError";
      } else if (error?.response?.data?.message) {
        message = error.response.data.message;
      } else if (error?.message) {
        message = error.message;
      }
      setApiError(message);
      console.error('Failed to send invitations:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const emailValidation = validateEmails(emails);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        {t('inviteUsers.title')}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mt: 2, alignItems: 'flex-start' }}>
          <Box sx={{ flex: '0 0 295px', minWidth: 260, maxWidth: 360 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.primary' }}>
              {t('inviteUsers.fields.locations')} <Typography component="span" sx={{ color: 'text.secondary' }}>{t('inviteUsers.labels.optional')}</Typography>
            </Typography>
            <LocationSelector
              selectedLocations={selectedLocations}
              onLocationSelect={handleLocationSelect}
              multiSelect={true}
              searchable={true}
              required={false}
              placeholder={t('inviteUsers.fields.locations')}
              disabled={false}
              expandSelectedNodes={false}
            />
          </Box>          
          <Box sx={{ flex: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.primary' }}>
                {t('inviteUsers.fields.emails')}
              </Typography>
              <TextField
                value={emails}
                onChange={(e) => {
                  setEmails(e.target.value);
                  if (errors.emails) {
                    setErrors(prev => ({ ...prev, emails: false }));
                  }
                }}
                fullWidth
                multiline
                minRows={4}
                maxRows={8}
                placeholder={t('inviteUsers.fields.emailsPlaceholder')}
                error={!!errors.emails}
                helperText={
                  errors.emails
                    ? !emails.trim()
                      ? t('inviteUsers.validation.emailsRequired')
                      : t('inviteUsers.validation.invalidEmails', { emails: emailValidation.invalidEmails.join(', ') })
                    : t('inviteUsers.fields.emailsHelp')
                }
                sx={{
                  marginTop: '3px',
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'monospace',
                  }
                }}
              />
            </Box>

            <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.jobFunction} variant="outlined">
              <InputLabel
                id="user-group-label"
                shrink
                sx={{
                  color: 'text.primary',
                  '&.Mui-focused': {
                    color: 'text.primary',
                  },
                  backgroundColor: 'background.paper',
                  px: 0.5,
                  transform: 'translate(14px, -9px) scale(0.75)',
                }}
              >
                {t('inviteUsers.fields.userGroup')} *
              </InputLabel>
              <Select
                label={t('inviteUsers.fields.userGroup')}
                labelId="user-group-label"
                value={userGroup}
                onChange={(e) => {
                  setUserGroup(e.target.value);
                  if (errors.jobFunction) {
                    setErrors(prev => ({ ...prev, jobFunction: false }));
                  }
                }}
                disabled={loadingUserGroups}
                displayEmpty
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    textAlign: 'left',
                    '& legend': {
                      visibility: 'visible',
                      width: 'auto',
                    },
                  },
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      maxWidth: '100vw',
                      minWidth: 200,
                      width: 'auto',
                      pl: 2,
                    },
                  },
                }}
              >
                {loadingUserGroups ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      {t('inviteUsers.messages.loadingUserGroups')}
                    </Box>
                  </MenuItem>
                ) : userGroups.length > 0 ? (
                  userGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id.toString()}>
                      {group.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>{t('inviteUsers.messages.loadingUserGroups')}</MenuItem>
                )}
              </Select>
              {errors.jobFunction && (
                <FormHelperText>
                  {t('inviteUsers.validation.userGroupRequired')}
                </FormHelperText>
              )}
            </FormControl>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.primary' }}>
                {t('inviteUsers.fields.contactInfo')}
              </Typography>
              <TextField
                value={contactInfo}
                onChange={(e) => setContactInfo(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                placeholder={t('inviteUsers.fields.contactInfoPlaceholder')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'inherit',
                  }
                }}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              {apiError && (
                <Typography
                  ref={errorRef}
                  tabIndex={-1}
                  color="error"
                  sx={{ mt: 1, fontSize: '0.875rem' }}
                  role="alert"
                >
                  {t(apiError, apiError)}
                </Typography>
              )}
              {successMessage && (
                <Typography
                  ref={successRef}
                  tabIndex={-1}
                  color="success.main"
                  sx={{ mt: 1, fontSize: '0.875rem' }}
                  role="status"
                >
                  {successMessage}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size='medium' color="primary" variant='text'>
          {t('inviteUsers.actions.cancel')}
        </Button>
        <Button
          onClick={(e) => handleSendInvitation(e)}
          size="medium"
          color="primary"
          variant="contained"
          disabled={isSubmitting || Object.values(errors).some(error => error)}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          type="button"
        >
          {isSubmitting ? 'SENDING...' : t('inviteUsers.actions.sendInvitations')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteUsers;